import { useId } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";
import { Badge } from "./ui";

export interface AmountFieldProps<T extends FieldValues, C = unknown, TT extends FieldValues = T> {
  control: Control<T, C, TT>;
  name: FieldPath<T>;
  label: string;
  kind?: "money" | "percent";
  hint?: string;
  placeholder?: string;
  /** Shows where the value comes from (preset vs. typed by the user). */
  origin?: "PROFILE" | "MANUAL";
  error?: string;
  currencySymbol?: string;
}

/** Large, touch-friendly numeric input bound to react-hook-form. */
export function AmountField<T extends FieldValues, C = unknown, TT extends FieldValues = T>({
  control,
  name,
  label,
  kind = "money",
  hint,
  placeholder = "0.00",
  origin,
  error,
  currencySymbol = "$",
}: AmountFieldProps<T, C, TT>) {
  const id = useId();
  const noteId = `${id}-note`;
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange, onBlur, ref } }) => (
        <div className="field">
          <div className="field__label-row">
            <label className="field__label" htmlFor={id}>
              {label}
            </label>
            {origin ? (
              <Badge label={origin === "PROFILE" ? "Preset" : "Manual"} tone={origin === "PROFILE" ? "info" : "warning"} />
            ) : null}
          </div>
          <div className={error ? "field__box field__box--error" : "field__box"}>
            {kind === "money" ? (
              <span className="field__affix" aria-hidden>
                {currencySymbol}
              </span>
            ) : null}
            <input
              id={id}
              ref={ref}
              className="field__input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              enterKeyHint="done"
              value={(value as string | undefined) ?? ""}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              onFocus={(e) => e.currentTarget.select()}
              placeholder={placeholder}
              aria-invalid={error ? true : undefined}
              aria-describedby={error || hint ? noteId : undefined}
            />
            {kind === "percent" ? (
              <span className="field__affix" aria-hidden>
                %
              </span>
            ) : null}
          </div>
          {error ? (
            <span id={noteId} className="field__error" aria-live="polite">
              {error}
            </span>
          ) : hint ? (
            <span id={noteId} className="field__hint">
              {hint}
            </span>
          ) : null}
        </div>
      )}
    />
  );
}
